









































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientSystemConfiguration, ClientSystemConfigurationValue } from "@/data/models/ClientAdministrationRow";
import { DEFAULT_MIRA_MODE, MiraMode } from "@/data/models/MiraMode";
import { setClientSystemConfiguration } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ChatMode } from "@/utils/ChatMode";
import {
  AssistMode,
  SYSTEM_CONFIGURATION_ROWS,
  SystemConfigurationRow,
  applySystemConfigurationDependencies,
  assistModeOf,
  chatModeOf,
  clientSystemConfigurationErrorKey,
  copySystemConfiguration,
  emptiesVehicles,
  isBlockedSwitch,
  rowOptions,
  setAssistMode,
  setChatMode,
  systemConfigurationDiffers,
  systemConfigurationSaveFeedback,
} from "@/utils/ClientSystemConfiguration";

/** What `show()` resolves with: saved (the caller reloads), CHANGED_MEANWHILE (the caller reloads), or null. */
export type SystemConfigurationDialogResult = "SAVED" | "CHANGED_MEANWHILE" | null;

const EMPTY_VALUE: ClientSystemConfigurationValue = {
  enableGeoEncoding: false,
  enableTracking: false,
  enableVehicleManagement: false,
  enableScheduling: false,
  enableAllDevicesManagement: false,
  enableDeviceManagementTile: false,
  enableTimeTracking: false,
  enablePersonalChat: false,
  enableProjectChat: false,
  enableDeviceCheckManagement: false,
  enableAiPredictions: false,
  enableBillingCatalogs: false,
  renoActivationScope: "ACTIVE_AND_NEW_ONLY",
  miraMode: DEFAULT_MIRA_MODE,
};

/**
 * Edits a client's Systemkonfiguration (#2610141): every setting of IRES Office's Einstellungen >
 * Systemkonfiguration plus Mira. The values are sent with the ones loaded, so IRES Cloud can refuse a change
 * made meanwhile; IRES Cloud also runs what the change sets off. Switching Fahrzeuge off asks first, as on
 * IRES Office's page, since it takes every device out of its vehicle.
 *
 * `show()` resolves with `SAVED`, `CHANGED_MEANWHILE` when IRES Cloud refused because the client changed
 * since (the caller reloads either way), or null when closed without saving.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientSystemConfigurationDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  rows = SYSTEM_CONFIGURATION_ROWS;
  busy = false;
  clientId = "";
  isMaster = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientSystemConfigurationValue = copySystemConfiguration(EMPTY_VALUE);
  form: ClientSystemConfigurationValue = copySystemConfiguration(EMPTY_VALUE);

  private resolve: ((result: SystemConfigurationDialogResult) => void) | null = null;

  get hasChanged(): boolean {
    return systemConfigurationDiffers(this.stored, this.form);
  }

  rowKey(row: SystemConfigurationRow): string {
    return row.kind === "switch" ? row.field : row.kind;
  }

  rowValue(row: SystemConfigurationRow): boolean | string {
    switch (row.kind) {
      case "switch":
        return this.form[row.field];
      case "chat":
        return chatModeOf(this.form);
      case "assist":
        return assistModeOf(this.form);
      case "mira":
        return this.form.miraMode;
    }
  }

  /** Each choice with the label IRES Office's page gives it. */
  itemsOf(row: SystemConfigurationRow): Array<{ value: boolean | string; label: string }> {
    return rowOptions(row).map((value) => ({ value, label: this.optionText(row, value) }));
  }

  private optionText(row: SystemConfigurationRow, value: boolean | string): string {
    switch (row.kind) {
      case "switch":
        return this.$t(value ? "common.yes" : "common.no").toString();
      case "chat":
        return this.$t(`settings.system.chat.mode.${value}`).toString();
      case "assist":
        return this.$t(`settings.aiPredictions.mode.${value}`).toString();
      case "mira":
        return this.$t(`settings.system.mira.mode.${value}`).toString();
    }
  }

  isBlocked(row: SystemConfigurationRow): boolean {
    return row.kind === "switch" && isBlockedSwitch(row.field, this.form);
  }

  onRowChange(row: SystemConfigurationRow, value: boolean | string) {
    switch (row.kind) {
      case "switch":
        this.form[row.field] = value as boolean;
        break;
      case "chat":
        setChatMode(this.form, value as ChatMode);
        break;
      case "assist":
        setAssistMode(this.form, value as AssistMode);
        break;
      case "mira":
        this.form.miraMode = value as MiraMode;
        break;
    }
    applySystemConfigurationDependencies(this.form);
  }

  /**
   * @param clientId - the client.
   * @param configuration - its Systemkonfiguration as the detail loaded it.
   * @param isMaster - whether it is a MASTER-Mandant, whose configuration its following members use too.
   */
  show(clientId: string, configuration: ClientSystemConfiguration, isMaster: boolean): Promise<SystemConfigurationDialogResult> {
    this.clientId = clientId;
    this.isMaster = isMaster;
    this.stored = copySystemConfiguration(configuration.value);
    this.form = copySystemConfiguration(configuration.value);
    this.baseDialog.show({
      title: this.$t("admin.clients.systemConfiguration.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 640,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged) {
      return;
    }
    // Fahrzeuge off empties every vehicle, so it asks first, as IRES Office's page does.
    if (emptiesVehicles(this.stored, this.form)) {
      const confirmed = await this.confirmDialog.show({
        title: this.$t("settings.system.moveDevicesDialog.title").toString(),
        message: this.$t("settings.system.moveDevicesDialog.message").toString(),
      });
      if (!confirmed) {
        return;
      }
    }
    this.busy = true;
    try {
      const result = await setClientSystemConfiguration(this.clientId, copySystemConfiguration(this.stored), copySystemConfiguration(this.form));
      const feedback = systemConfigurationSaveFeedback(result.assistActivation, result.effectsFailed, "admin.clients.systemConfiguration.saved");
      if (feedback.kind === "error") {
        SnackbarModule.showError({ key: feedback.key });
      } else {
        SnackbarModule.showSuccess({ key: feedback.key });
      }
      this.close("SAVED");
    } catch (error) {
      const key = clientSystemConfigurationErrorKey(error);
      SnackbarModule.showError({ key });
      // Someone changed the client meanwhile: close, so the caller reloads from the new values.
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: SystemConfigurationDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: SystemConfigurationDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
